import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  SiReact,
  SiJavascript,
  SiCss3,
  SiBootstrap,
  SiTailwindcss,
  SiVite,
  SiPython,
  SiCplusplus,
  SiMongodb,
  SiMysql,
  SiArduino,
  SiDocker,
  SiNodedotjs,
  SiNextdotjs,
  SiPostgresql,
  SiRedis,
  SiTypescript,
} from "react-icons/si";
import {
  FaBrain,
  FaNetworkWired,
  FaComments,
  FaServer,
  FaAdjust,
  FaChalkboardTeacher,
  FaRobot,
  FaGoogle,
  FaJava,
  FaResearchgate,
} from "react-icons/fa";
import { MdSecurity } from "react-icons/md";
import "./Skill.css";

const Skill = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const skills = {
    frontend: [
      { name: "React JS", icon: <SiReact />, level: 80, color: "#61DAFB" },
      {
        name: "JavaScript",
        icon: <SiJavascript />,
        level: 80,
        color: "#F7DF1E",
      },
      { name: "CSS3", icon: <SiCss3 />, level: 80, color: "#E34F26" },
      { name: "Bootstrap", icon: <SiBootstrap />, level: 75, color: "#7952B3" },
      { name: "Vite", icon: <SiVite />, level: 75, color: "#646CFF" },
      { name: "Next.js", icon: <SiNextdotjs />, level: 75, color: "#818185" },
      {
        name: "TypeScript",
        icon: <SiTypescript />,
        level: 70,
        color: "#4082d8",
      },
      {
        name: "Tailwind CSS",
        icon: <SiTailwindcss />,
        level: 85,
        color: "#38B2AC",
      },
    ],
    backend: [
      { name: "Python", icon: <SiPython />, level: 80, color: "#3776AB" },
      { name: "C++", icon: <SiCplusplus />, level: 70, color: "#0e3783" },
      { name: "Java", icon: <FaJava />, level: 70, color: "#007396" },
      { name: "MongoDB", icon: <SiMongodb />, level: 80, color: "#47A248" },
      { name: "MySQL", icon: <SiMysql />, level: 80, color: "#4479A1" },
      {
        name: "PostgreSql",
        icon: <SiPostgresql />,
        level: 85,
        color: "#2f61a1",
      },
      { name: "Redis", icon: <SiRedis />, level: 75, color: "#DC382D" },
      { name: "Node.js", icon: <SiNodedotjs />, level: 80, color: "#339933" },
    ],
    others: [
      {
        name: "Critical Thinking",
        icon: <FaBrain />,
        level: 90,
        color: "#667eea",
      },
      {
        name: "Network Security",
        icon: <MdSecurity />,
        level: 70,
        color: "#f5576c",
      },
      { name: "IoT", icon: <FaNetworkWired />, level: 80, color: "#00f2fe" },
      {
        name: "Communication",
        icon: <FaComments />,
        level: 90,
        color: "#a78bfa",
      },
      { name: "Arduino", icon: <SiArduino />, level: 80, color: "#00979D" },
      { name: "Docker", icon: <SiDocker />, level: 75, color: "#2496ED" },
      { name: "Kafka", icon: <FaServer />, level: 70, color: "#ada0a3" },
      { name: "Adaptability", icon: <FaAdjust />, level: 90, color: "#f7971e" },
      {
        name: "Quick Learner",
        icon: <FaChalkboardTeacher />,
        level: 90,
        color: "#fbd786",
      },
      {
        name: "Claude/Anthropic",
        icon: <FaRobot />,
        level: 95,
        color: "#00c9ff",
      },
      {
        name: "Google Gemini",
        icon: <FaGoogle />,
        level: 90,
        color: "#45cf6c",
      },
      {
        name: "Research Skill",
        icon: <FaResearchgate />,
        level: 90,
        color: "#d9df25",
      },
    ],
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const SkillCard = ({ skill, i }) => (
    <motion.div
      className="skill-card glass-card"
      variants={itemVariants}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <div className="skill-icon" style={{ color: skill.color }}>
        {skill.icon}
      </div>
      <h4 className="skill-name">{skill.name}</h4>
      <div className="skill-progress-container">
        <motion.div
          className="skill-progress-bar"
          initial={{ width: 0 }}
          animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
          transition={{ duration: 1, delay: i * 0.1, ease: "easeOut" }}
          style={{
            background: `linear-gradient(90deg, ${skill.color}80, ${skill.color})`,
          }}
        />
      </div>
      <span className="skill-level">{skill.level}%</span>
    </motion.div>
  );

  return (
    <section className="skill-section" id="skill" ref={ref}>
      <div className="container">
        {/* Header */}
        <motion.div
          className="skill-header"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="section-badge"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            About Me
          </motion.div>
          <h2 className="section-title">
            <span className="gradient-text">Skills & Expertise</span>
          </h2>
          <p className="section-description">
            As someone who is passionate and dedicated in machine learning, AI,
            and web development, I am committed to continuous learning and
            improvement. My unwavering determination ensures that I remain
            focused and fiery-eagerness, driven to enhance my skills as a
            developer and AI engineer, along as someone who can provide support
            in IT field and solve technical problems. I am always eager to learn
            from many challenges and people whom i will work with. 👏👏👏
          </p>
        </motion.div>

        {/* Frontend Skills */}
        <motion.div
          className="skill-category"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h3 className="category-title" variants={itemVariants}>
            Frontend Development
          </motion.h3>
          <div className="skills-grid">
            {skills.frontend.map((skill, i) => (
              <SkillCard key={skill.name} skill={skill} i={i} />
            ))}
          </div>
        </motion.div>

        {/* Backend Skills */}
        <motion.div
          className="skill-category"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h3 className="category-title" variants={itemVariants}>
            Backend Development
          </motion.h3>
          <div className="skills-grid">
            {skills.backend.map((skill, i) => (
              <SkillCard key={skill.name} skill={skill} i={i} />
            ))}
          </div>
        </motion.div>

        {/* Other Skills */}
        <motion.div
          className="skill-category"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h3 className="category-title" variants={itemVariants}>
            Other Skills
          </motion.h3>
          <div className="skills-grid">
            {skills.others.map((skill, i) => (
              <SkillCard key={skill.name} skill={skill} i={i} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skill;
